// src/pages/ManageParkingSlotsPage.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import {  IconButton, Button } from '@mui/material';
import { Power as PowerIcon, Delete as DeactivateIcon, Visibility as ViewIcon } from '@mui/icons-material';
import {
  Typography,
  Box,
  Paper,
  TextField,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  fetchParkingSlots,
  addParkingSlot,
  removeParkingSlot,
  toggleCharger,
  updateParkingSlotsOrder,
  assignParkingSlot
} from '../api/parkingSlots';
import SnackbarAlert from '../components/SnackbarAlert';
import { fetchUsers } from '../api/users';

function ManageParkingSlotsPage() {
  const navigate = useNavigate();
  const [parkingSlots, setParkingSlots] = useState([]);
  const [slotNumber, setSlotNumber] = useState('');
  const [address, setAddress] = useState('');
  const [hasCharger, setHasCharger] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [users, setUsers] = useState([]);

  const existingAddresses = [...new Set(parkingSlots.map(s => s.address))];

  const loadSlots = async () => {
    try {
      const data = await fetchParkingSlots();
      setParkingSlots(data.parking_slots || []);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const loadUsers = async () => {
    try {
      const data = await fetchUsers();
      setUsers(data.users || []);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleAddSlot = async () => {
    try {
      const data = await addParkingSlot({ address, slot_number: slotNumber, has_charger: hasCharger });
      setSnackbar({ open: true, message: data.message, severity: 'success' });
      // Reset form
      setSlotNumber('');
      setAddress('');
      setHasCharger(false);
      loadSlots();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleRemoveSlot = async (slotId) => {
    try {
      const data = await removeParkingSlot(slotId);
      setSnackbar({ open: true, message: data.message, severity: 'success' });
      loadSlots();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleToggleCharger = async (slotId) => {
    try {
      const data = await toggleCharger(slotId);
      setSnackbar({ open: true, message: data.message, severity: 'success' });
      loadSlots();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  const handleAssign = async (slotId, userIdOrNull) => {
    try {
      const data = await assignParkingSlot(slotId, userIdOrNull);
      setSnackbar({ open: true, message: data.message, severity: 'success' });
      loadSlots();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  // Handle drag & drop reordering
  const onDragEnd = async (result) => {
    if (!result.destination) return; // dropped outside the list
    const newSlots = Array.from(parkingSlots);
    const [moved] = newSlots.splice(result.source.index, 1);
    newSlots.splice(result.destination.index, 0, moved);

    setParkingSlots(newSlots);

    // After local reorder, update back-end
    try {
      const orderedIds = newSlots.map(slot => slot.id);
      await updateParkingSlotsOrder(orderedIds);
      setSnackbar({ open: true, message: 'Parking slots order updated successfully.', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  useEffect(() => {
    loadSlots();
    loadUsers();
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Create a Slot</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            label="Slot Number"
            value={slotNumber}
            onChange={(e) => setSlotNumber(e.target.value)}
          />

          {/* Use Autocomplete for address suggestions + free text */}
          <Autocomplete
            freeSolo
            options={existingAddresses} // distinct addresses
            inputValue={address}
            onInputChange={(event, newInputValue) => setAddress(newInputValue)}
            onChange={(event, newValue) => setAddress(newValue || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Address"
                placeholder="Enter or select an address"
              />
            )}
            sx={{ width: 300 }}
          />

          <FormControlLabel
            control={
              <Checkbox checked={hasCharger} onChange={() => setHasCharger(!hasCharger)} />
            }
            label="Has Charger?"
          />

          <Button variant="contained" onClick={handleAddSlot}>
            Add / Reactivate Slot
          </Button>
        </Box>
      </Paper>

      {/* Slots List with Drag & Drop */}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="slotsList">
          {(provided) => (
            <Box {...provided.droppableProps} ref={provided.innerRef}>
              {parkingSlots.map((slot, index) => (
                <Draggable key={slot.id} draggableId={String(slot.id)} index={index}>
                  {(provided2) => (
                    <Paper
                      ref={provided2.innerRef}
                      {...provided2.draggableProps}
                      {...provided2.dragHandleProps}
                      sx={{ display: 'flex', alignItems: 'center', p: 2, mb: 1, gap: 2 }}
                    >
                      <Box flex={1}>
                        <Typography>
                          {slot.slot_number} at {slot.address}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Assigned to: {slot.assigned_user_email || '—'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ViewIcon />}
                          onClick={() => navigate(`/parking-slots/${slot.id}`)}
                        >
                          View Details
                        </Button>
                        <Autocomplete
                          size="small"
                          sx={{ width: 260 }}
                          options={[{ id: null, email: 'Unassigned' }, ...users]}
                          getOptionLabel={(option) => option.email}
                          value={slot.assigned_user_id ? users.find(u => u.id === slot.assigned_user_id) || null : { id: null, email: 'Unassigned' }}
                          onChange={(e, value) => handleAssign(slot.id, value?.id ?? null)}
                          renderInput={(params) => <TextField {...params} label="Assign user" />}
                        />
                        <Tooltip title="Electric car charger" arrow>
                          <IconButton
                            sx={{
                              color: slot.has_charger ? 'primary.main' : 'grey.300',
                            }}
                            onClick={() => handleToggleCharger(slot.id)}
                            aria-label="Electric car charger"
                          >
                            <PowerIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Deactivate Slot" arrow>
                          <IconButton
                            sx={{
                              color: 'error.main',
                            }}
                            onClick={() => handleRemoveSlot(slot.id)}
                            aria-label="Deactivate Slot"
                          >
                            <DeactivateIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Paper>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      <SnackbarAlert 
        open={snackbar.open} 
        message={snackbar.message} 
        severity={snackbar.severity} 
        onClose={() => setSnackbar({ ...snackbar, open: false })} 
      />
    </Box>
  );
}

export default ManageParkingSlotsPage;
